'use client';
import React, { useState } from 'react';
import { CheckCircle, AlertTriangle, XCircle, Info, Zap, ChevronDown, ChevronRight, Lightbulb,  } from 'lucide-react';
import type { CircuitResult, PlacedComponent, Wire } from './circuitTypes';
import { COMPONENT_DEFINITIONS, WIRE_COLOR_MAP } from './circuitTypes';

interface Props {
  result: CircuitResult | null;
  components: PlacedComponent[];
  wires: Wire[];
  isLoading: boolean;
  selectedComponentId: string | null;
  onSelectComponent: (id: string | null) => void;
}

const STATUS_CONFIG = {
  idle: {
    label: 'Not tested yet',
    icon: Info,
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    text: 'text-slate-600',
    iconColor: 'text-slate-400',
  },
  correct: {
    label: 'Correct & Safe',
    icon: CheckCircle,
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-700',
    iconColor: 'text-green-500',
  },
  incorrect: {
    label: 'Incorrect Wiring',
    icon: AlertTriangle,
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    iconColor: 'text-amber-500',
  },
  unsafe: {
    label: 'Unsafe Wiring',
    icon: XCircle,
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-700',
    iconColor: 'text-red-500',
  },
  'short-circuit': {
    label: 'Short Circuit!',
    icon: Zap,
    bg: 'bg-pink-50',
    border: 'border-pink-200',
    text: 'text-pink-700',
    iconColor: 'text-pink-500',
  },
  incomplete: {
    label: 'Circuit Incomplete',
    icon: Info,
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    text: 'text-slate-600',
    iconColor: 'text-slate-400',
  },
};

const FAULT_SEVERITY_CONFIG = {
  error: { bg: 'bg-red-50', border: 'border-red-100', icon: XCircle, iconColor: 'text-red-500', text: 'text-red-700' },
  warning: { bg: 'bg-amber-50', border: 'border-amber-100', icon: AlertTriangle, iconColor: 'text-amber-500', text: 'text-amber-700' },
  info: { bg: 'bg-blue-50', border: 'border-blue-100', icon: Info, iconColor: 'text-blue-500', text: 'text-blue-700' },
};

const HINTS: Record<string, string> = {
  supply: 'Connect the Supply L terminal through an MCB to the circuit live. Connect N to the neutral bar. Connect E to the earth bar.',
  lamp: 'The lamp needs: Live (brown) in, Neutral (blue) return, and Earth (green/yellow) for protection.',
  'switch-one-way': 'Wire the switch COM terminal from the supply live side. Wire L1 to the lamp live terminal. The switch must be in the LIVE conductor only.',
  mcb: 'Connect supply live to MCB IN terminal. MCB OUT connects to the circuit live conductor.',
  general: 'Start with the supply, add protection (MCB/RCBO), then add your load (lamp/socket). Always connect earth to all metalwork.',
};

function HintSection() {
  const [open, setOpen] = useState(false);
  const [selectedHint, setSelectedHint] = useState<string>('general');

  return (
    <div className="border border-amber-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between w-full px-3 py-2.5 bg-amber-50 text-amber-700 text-xs font-semibold hover:bg-amber-100 transition-colors duration-150"
      >
        <span className="flex items-center gap-1.5">
          <Lightbulb size={13} /> Hints
        </span>
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
      </button>
      {open && (
        <div className="px-3 py-3 bg-white">
          <div className="flex flex-wrap gap-1.5 mb-2">
            {Object.keys(HINTS).map((key) => (
              <button
                key={`hint-${key}`}
                onClick={() => setSelectedHint(key)}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors duration-150 ${
                  selectedHint === key
                    ? 'bg-amber-100 text-amber-700 border border-amber-300' :'bg-muted text-muted-foreground hover:bg-amber-50'
                }`}
              >
                {key === 'general' ? 'General' : key.replace('-', ' ')}
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-600 leading-relaxed">{HINTS[selectedHint]}</p>
        </div>
      )}
    </div>
  );
}

export default function CircuitAnalysisPanel({
  result, components, wires, isLoading,
  selectedComponentId, onSelectComponent,
}: Props) {
  const status = result?.status ?? 'idle';
  const cfg = STATUS_CONFIG[status];
  const StatusIcon = cfg.icon;

  const selectedComp = components.find((c) => c.instanceId === selectedComponentId);
  const def = selectedComp ? COMPONENT_DEFINITIONS[selectedComp.type] : null;

  return (
    <aside className="w-72 bg-white border-l border-border flex flex-col flex-shrink-0 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border">
        <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Circuit Analysis</h2>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Status banner */}
        <div className={`mx-3 mt-3 rounded-xl border p-3.5 ${cfg.bg} ${cfg.border}`}>
          <div className="flex items-center gap-2 mb-1">
            <StatusIcon size={16} className={cfg.iconColor} />
            <span className={`text-sm font-bold ${cfg.text}`}>{cfg.label}</span>
          </div>
          {isLoading && (
            <div className="flex items-center gap-2 mt-2">
              <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full animate-pulse w-2/3" />
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap">Analysing...</span>
            </div>
          )}
          {!isLoading && !result && (
            <p className="text-xs text-muted-foreground mt-1">
              Press <span className="font-semibold text-primary">Test Circuit</span> to analyse your wiring
            </p>
          )}
        </div>

        {/* Fault messages */}
        {result && result.faults.length > 0 && (
          <div className="px-3 mt-3 flex flex-col gap-2">
            {result.faults.map((fault) => {
              const fc = FAULT_SEVERITY_CONFIG[fault.severity];
              const FaultIcon = fc.icon;
              return (
                <div
                  key={`fault-${fault.code}`}
                  className={`rounded-lg border p-3 ${fc.bg} ${fc.border} animate-fade-in`}
                >
                  <div className="flex items-start gap-2">
                    <FaultIcon size={13} className={`${fc.iconColor} flex-shrink-0 mt-0.5`} />
                    <div>
                      <p className={`text-xs font-semibold mb-1 ${fc.text}`}>{fault.title}</p>
                      <p className="text-xs text-slate-600 leading-relaxed">{fault.message}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Wire summary */}
        {wires.length > 0 && (
          <div className="px-3 mt-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2">Wire Summary</p>
            <div className="flex gap-2">
              {(['live', 'neutral', 'earth'] as const).map((color) => {
                const count = wires.filter((w) => w.color === color).length;
                const cfg = WIRE_COLOR_MAP[color];
                return (
                  <div key={`summary-${color}`} className="flex-1 text-center py-2 rounded-lg bg-slate-50 border border-border">
                    <div className="w-2.5 h-2.5 rounded-full mx-auto mb-1" style={{ backgroundColor: cfg.hex }} />
                    <div className="text-sm font-bold tabular-nums text-foreground">{count}</div>
                    <div className="text-xs text-muted-foreground/70 capitalize">{color}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Selected component info */}
        {selectedComp && def && (
          <div className="px-3 mt-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2">Selected Component</p>
            <div className="bg-slate-50 border border-border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold text-foreground">{selectedComp.label}</span>
                <span className="text-xs font-mono text-muted-foreground bg-white px-2 py-0.5 rounded border border-border">
                  {def.label}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                {def.terminals.map((t, idx) => {
                  const termColor = WIRE_COLOR_MAP[t.color].hex;
                  const connectedWires = wires.filter(
                    (w) =>
                      (w.fromComponentId === selectedComp.instanceId && w.fromTerminalId === `${selectedComp.instanceId}-t${idx}`) ||
                      (w.toComponentId === selectedComp.instanceId && w.toTerminalId === `${selectedComp.instanceId}-t${idx}`)
                  );
                  return (
                    <div key={`term-info-${idx}`} className="flex items-center gap-2 text-xs">
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: termColor }}
                      />
                      <span className="font-mono font-semibold text-foreground w-8">{t.label}</span>
                      <span className="text-muted-foreground flex-1 capitalize">{t.color}</span>
                      <span className={`text-xs font-medium ${connectedWires.length > 0 ? 'text-green-600' : 'text-slate-400'}`}>
                        {connectedWires.length > 0 ? `${connectedWires.length} wire${connectedWires.length > 1 ? 's' : ''}` : 'open'}
                      </span>
                    </div>
                  );
                })}
              </div>
              {selectedComp.type === 'switch-one-way' || selectedComp.type === 'switch-two-way' ? (
                <div className="mt-2 pt-2 border-t border-border flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">State:</span>
                  <span className={`text-xs font-semibold ${selectedComp.switchState ? 'text-green-600' : 'text-slate-500'}`}>
                    {selectedComp.switchState ? 'ON (closed)' : 'OFF (open)'}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        )}

        {/* Hints */}
        <div className="px-3 mt-4 mb-3">
          <HintSection />
        </div>

        {/* UK wiring reference */}
        <div className="px-3 mb-4">
          <div className="rounded-lg border border-border p-3 bg-slate-50">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2">UK Wire Colours</p>
            <div className="flex flex-col gap-1.5">
              {(['live', 'neutral', 'earth'] as const).map((color) => {
                const cfg = WIRE_COLOR_MAP[color];
                return (
                  <div key={`ref-${color}`} className="flex items-center gap-2 text-xs">
                    <span className="w-8 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cfg.hex }} />
                    <span className="font-medium text-foreground">{cfg.label}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground/70 mt-2 leading-relaxed">
              Per BS 7671:2018 (18th Edition IET Wiring Regulations)
            </p>
          </div>
        </div>
      </div>
    </aside>
  );
}